// src/shared/components/print/PrintableBooking.jsx
// ✅ تاريخ موحد DD/MM/YYYY
import React, { forwardRef } from 'react';
import styles from './PrintableReport.module.css';
import { formatNumber, formatDate } from '../../utils/formatters';

const getBookingTypeDisplay = (type) => ({
  graduation:        'حفلات التخرج',
  kids:              'تصوير أطفال',
  event:             'مناسبات',
  wedding:           'الأعراس',
  special:           'طلب خاص',
  digital_marketing: 'تسويق إلكتروني',
}[type] || type);

const PrintableBooking = forwardRef(({ booking, settings }, ref) => {
  if (!booking || !settings) return null;

  const { graduation_details, wedding_details, kids_details, event_details } = booking;

  const bookingTypeTitle = {
    graduation:        'تفاصيل حجز التخرج',
    wedding:           'تفاصيل حجز العرس',
    kids:              'تفاصيل حجز الأطفال',
    event:             'تفاصيل المناسبة',
    special:           'تفاصيل طلب خاص',
    digital_marketing: 'تفاصيل التسويق الإلكتروني',
  };

  const extraRevenues = [];
  if (graduation_details) {
    if (parseFloat(graduation_details.extra_photos_revenue) > 0)
      extraRevenues.push({ label: 'صور إضافية', details: `${graduation_details.extra_photos_count} × ${formatNumber(graduation_details.extra_photo_price)}`, total: formatNumber(graduation_details.extra_photos_revenue) });
    if (parseFloat(graduation_details.other_revenues) > 0)
      extraRevenues.push({ label: 'إيرادات أخرى', details: '-', total: formatNumber(graduation_details.other_revenues) });
    if (parseFloat(graduation_details.additional_revenues) > 0)
      extraRevenues.push({ label: 'إضافات أخرى', details: '-', total: formatNumber(graduation_details.additional_revenues) });
  }

  const totalOffersRevenue = graduation_details?.offers?.reduce(
    (sum, offer) => sum + parseFloat(offer.offer_subtotal || 0), 0
  ) || 0;

  const totalStudents = graduation_details?.offers?.reduce(
    (sum, offer) => sum + parseInt(offer.num_students || 0), 0
  ) || 0;

  return (
    <div ref={ref} className={styles.printContainer}>

      <header className={styles.printHeader}>
        {settings.banner && (
          <img src={settings.banner} alt="Company Banner" className={styles.bannerImage} />
        )}
      </header>

      <main className={styles.printContent}>
        <h1 className={styles.mainTitle}>تفاصيل الحجز</h1>
        <h2 className={styles.subtitle}>العميل: {booking.client?.name}</h2>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>المعلومات الأساسية</h2>
          <div className={styles.grid}>
            <p><strong>اسم العميل:</strong> {booking.client?.name}</p>
            <p><strong>رقم الهاتف:</strong> {booking.client?.phone || '—'}</p>
            <p><strong>نوع الحجز:</strong> {getBookingTypeDisplay(booking.booking_type)}</p>
            <p><strong>تاريخ الحجز:</strong> {formatDate(booking.booking_date)}</p>
            <p><strong>موقع العمل:</strong> {booking.shooting_location || '—'}</p>
            {booking.shooting_time && (
              <p><strong>وقت التصوير:</strong> {booking.shooting_time?.slice(0,5)}</p>
            )}
          </div>
          {booking.notes && (
            <>
              <h3 style={{ marginTop: '15px', fontSize: '11pt', borderBottom: '1px dashed #ccc', paddingBottom: '5px' }}>
                ملاحظات عامة:
              </h3>
              <p className={styles.notes}>{booking.notes}</p>
            </>
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{bookingTypeTitle[booking.booking_type]}</h2>

          {booking.booking_type === 'graduation' && graduation_details && (
            <>
              <div className={styles.grid}>
                <p><strong>الكلية:</strong> {graduation_details.college}</p>
                <p><strong>القسم:</strong> {graduation_details.department || '—'}</p>
                <p><strong>اسم الممثل:</strong> {graduation_details.representative_name || '—'}</p>
                <p><strong>رقم الممثل:</strong> {graduation_details.representative_phone || '—'}</p>
              </div>
              {extraRevenues.length > 0 && (
                <div className={styles.tableContainer} style={{ marginTop: '15px' }}>
                  <h3 style={{ fontSize: '11pt', marginBottom: '10px' }}>الإيرادات الإضافية:</h3>
                  <table className={styles.reportTable}>
                    <thead><tr><th>البيان</th><th>التفاصيل</th><th>الإجمالي</th></tr></thead>
                    <tbody>
                      {extraRevenues.map((row, i) => (
                        <tr key={i}><td>{row.label}</td><td>{row.details}</td><td className={styles.amountCell}>{row.total}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className={styles.tableContainer} style={{ marginTop: '15px' }}>
                <h3 style={{ fontSize: '11pt', marginBottom: '10px' }}>تفاصيل العروض:</h3>
                <table className={`${styles.reportTable} ${styles.offersTable}`}>
                  <thead>
                    <tr>
                      <th>العرض</th><th>عدد الطلاب</th><th>سعر العرض</th><th>الإجمالي</th>
                    </tr>
                  </thead>
                  <tbody>
                    {graduation_details.offers?.map((offer, i) => (
                      <tr key={offer.id || i}>
                        <td>{offer.name}</td>
                        <td>{offer.num_students}</td>
                        <td className={styles.amountCell}>{formatNumber(offer.offer_price)}</td>
                        <td className={styles.amountCell}>{formatNumber(offer.offer_subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td><strong>المجموع الكلي</strong></td>
                      <td><strong>{totalStudents}</strong></td>
                      <td></td>
                      <td className={styles.amountCell}><strong>{formatNumber(totalOffersRevenue)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}

          {booking.booking_type === 'wedding' && wedding_details && (
            <div className={styles.grid}>
              <p><strong>اسم العريس:</strong> {wedding_details.groom_name || '—'}</p>
              <p><strong>اسم العروس:</strong> {wedding_details.bride_name || '—'}</p>
            </div>
          )}

          {booking.booking_type === 'kids' && kids_details && (
            <div className={styles.grid}>
              <p><strong>اسم الطفل:</strong> {kids_details.child_name || '—'}</p>
              <p><strong>عمر الطفل:</strong> {kids_details.child_age || '—'}</p>
              <p><strong>الجنس:</strong> {kids_details.child_gender === 'boy' ? 'ولد' : 'بنت'}</p>
              <p><strong>ثيم الجلسة:</strong> {kids_details.session_theme || '—'}</p>
            </div>
          )}

          {booking.booking_type === 'event' && event_details && (
            <div className={styles.grid}>
              <p><strong>اسم المناسبة:</strong> {event_details.event_name || '—'}</p>
              <p><strong>عدد الحضور:</strong> {event_details.expected_attendees || '—'}</p>
            </div>
          )}
        </section>

        {/* الدفعات */}
        {booking.payments?.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>الدفعات ({booking.payments.length})</h2>
            <table className={styles.reportTable}>
              <thead>
                <tr><th>التاريخ</th><th>المبلغ</th><th>ملاحظات</th></tr>
              </thead>
              <tbody>
                {booking.payments.map(p => (
                  <tr key={p.id}>
                    <td>{formatDate(p.payment_date)}</td>
                    <td className={styles.amountCell}>{formatNumber(p.amount)} د.ع</td>
                    <td>{p.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* الملخص المالي */}
        <section className={styles.section} style={{ pageBreakInside: 'avoid' }}>
          <h2 className={styles.sectionTitle}>الملخص المالي</h2>
          <div className={styles.tableContainer}>
            <table className={styles.reportTable}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: '600', background: '#f8fafc' }}>الإجمالي الكلي:</td>
                  <td className={styles.amountCell} style={{ fontWeight: '700', fontSize: '12pt' }}>
                    {formatNumber(graduation_details?.grand_total || booking.total_price)} د.ع
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: '600', background: '#f8fafc' }}>إجمالي المدفوع:</td>
                  <td className={styles.amountCell} style={{ fontWeight: '700', color: '#10b981' }}>
                    {formatNumber(booking.total_paid)} د.ع
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: '600', background: '#f8fafc' }}>المبلغ المتبقي:</td>
                  <td className={styles.amountCell} style={{ fontWeight: '700', color: '#ef4444' }}>
                    {formatNumber(booking.remaining_amount)} د.ع
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <footer className={styles.printFooter}>
        <hr className={styles.footerLine} />
        <div className={styles.footerContent}>
          {settings.company_name && <p>{settings.company_name}</p>}
          {settings.phone_number && <p>{settings.phone_number}</p>}
          {settings.address     && <p>{settings.address}</p>}
        </div>
      </footer>

    </div>
  );
});

PrintableBooking.displayName = 'PrintableBooking';
export default PrintableBooking;